<template>
  <div
    :class="{
      'text-one-column': true,
    }"
    :style="`--currentColor:${textColor}; --done-image: url('${doneImageSrc}');`"
  >
    <div :class="{ 'container mx-auto p-0': true }">
      <VuePortableText
        :value="props.portableData"
        :components="myPortableTextComponents"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { PortableText as VuePortableText } from '@portabletext/vue';
import type { PortableTextComponents } from '@portabletext/vue';
import type { Slots } from 'vue';
import InternalLink from '../InternalLink/InternalLink.vue';
import ExternalLink from '../ExternalLink/ExternalLink.vue';
import PullQuotes from '../PullQuotes/PullQuotes.vue';
import { componentMap } from '~/components/Mapper';
import type { PortableTextBlock } from '@sanity/types';

const myPortableTextComponents: PortableTextComponents = {
  list: {
    bullet: (_, { slots }) =>
      h('ul', { class: 'ml-5 pl-5 mb-10 list-disc leading-8' }, slots.default?.()),
    number: (_, { slots }) =>
      h('ol', { class: 'ml-5 pl-5 mb-10 list-decimal leading-8' }, slots.default?.()),
    styledNumbered: (_, { slots }) =>
      h('ol', { class: 'styled-number-list ml-5 pl-5 mb-10 leading-8' }, slots.default?.()),
    checkMark: (_, { slots }) =>
      h(
        'ul',
        {
          class: 'text-left',
        },
        slots.default?.()
      ),
  },
  listItem: {
    bullet: (_, { slots }) => h('li', { style: { listStyleType: 'disc' } }, slots.default?.()),
    number: (_, { slots }) => h('li', { class: 'relative pl-4' }, slots.default?.()),
    styledNumbered: (_, { slots }) => h('li', { class: 'relative mb-8 pl-12' }, slots.default?.()),
    checkMark: (_, { slots }) =>
      h(
        'li',
        {
          class:
            'checklist-item mb-3 flex before:inline-block text-xs font-medium before:mr-5px before:h-18px before:w-18px before:min-w-15px',
        },
        [h('span', {}, slots.default?.())]
      ),
  },
  hardBreak: () => {
    return h('br');
  },
  types: componentMap,
  block: {
    normal: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'p',
        {
          value: value.text,
          class: 'content text-lg leading-8 mb-3 lg:mb-25px break-words last:mb-0',
        },
        slots.default?.() ?? value.text
      );
    },
    p: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'p',
        {
          value: value.text,
          class: 'content text-lg leading-8 mb-3 lg:mb-25px break-words last:mb-0',
        },
        slots.default?.() ?? value.text
      );
    },
    div: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'div',
        {
          value: value.text,
        },
        slots.default?.()
      );
    },
    h1: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'h1',
        {
          value: value.text,
          class:
            'text-28px lg:text-42px xl:text-60px leading-8 lg:leading-[4.3125rem] font-black pb-4',
        },
        slots.default?.()
      );
    },
    h2: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'h2',
        {
          value: value.text,
          class: 'text-28px  lg:text-42px font-medium leading-[3rem] font-black pb-4',
        },
        slots.default?.()
      );
    },
    h3: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'h3',
        {
          value: value.text,
          class: 'text-28px leading-10 font-black pb-4',
        },
        slots.default?.()
      );
    },
    h4: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'h4',
        {
          value: value.text,
          class: 'text-2xl leading-8 font-black pb-4',
        },
        slots.default?.()
      );
    },
    h5: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'h5',
        {
          value: value.text,
          class: 'text-xl leading-[3rem] font-black pb-4',
        },
        slots.default?.()
      );
    },
    h6: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'h6',
        {
          value: value.text,
          class: 'text-sm font-extrabold pb-4',
        },
        slots.default?.()
      );
    },
    blockquote: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'q',
        {
          value: value.text,
          class: 'ms-30px',
        },
        slots.default?.()
      );
    },
    disclaimerText: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        'disclaimerText',
        {
          value: value.text,
          class: 'text-12px italic font-black mb-4',
        },
        slots.default?.()
      );
    },
    pullQuoteText: ({ value }: { value: { text: string } }, { slots }: { slots: Slots }) => {
      return h(
        PullQuotes,
        {
          ...value,
        },
        slots.default?.()
      );
    },
  },
  marks: {
    link: ({ value }, { slots }: { slots: Slots }) => {
      const rel = !value.href.startsWith('/') ? 'noreferrer noopener' : undefined;
      return h(
        'a',
        {
          class: 'text-www-color-primary hover:text-www-color-primary-lighter underline',
          href: value.href,
          rel,
        },
        slots.default?.()
      );
    },
    textColor: ({ value }, { slots }: { slots: Slots }) => {
      textColor.value = value.value ? value.value : '#40aa1d'; // --www-color-primary-lighter
      return h(
        'span',
        { style: `color:${value.value};`, class: 'custom-underline-color' },
        slots.default?.()
      );
    },
    highlightColor: ({ value }, { slots }: { slots: Slots }) => {
      return h('span', { style: `background:${value.value}` }, slots.default?.());
    },
    internalLink: ({ value }, { slots }: { slots: Slots }) => {
      return h(
        InternalLink,
        {
          ...value,
          class:
            'internal-link text-www-color-primary hover:text-www-color-primary-lighter underline',
        },
        { default: () => slots.default?.() }
      );
    },
    externalLink: ({ value }, { slots }: { slots: Slots }) => {
      return h(
        ExternalLink,
        {
          ...value,
          class: 'text-www-color-primary hover:text-www-color-primary-lighter underline',
        },
        { default: () => slots.default?.() }
      );
    },
    left: (_, { slots }: { slots: Slots }) => {
      return h('span', { class: 'text-left block' }, slots.default?.());
    },
    center: (_, { slots }: { slots: Slots }) => {
      return h('span', { class: 'text-center block' }, slots.default?.());
    },
    right: (_, { slots }: { slots: Slots }) => {
      return h('span', { class: 'text-right block' }, slots.default?.());
    },
    justify: (_, { slots }: { slots: Slots }) => {
      return h('span', { class: 'text-justify block' }, slots.default?.());
    },
    anchorPoint: ({ value }, { slots }: { slots: Slots }) => {
      return h(
        'span',
        {
          id: value.id,
          class: 'inline',
        },
        { default: () => slots.default?.() }
      );
    },
    sup: (_, { slots }: { slots: Slots }) => {
      return h('sup', {}, slots.default?.());
    },
    sub: (_, { slots }: { slots: Slots }) => {
      return h('sub', {}, slots.default?.());
    },

    em: (_, { slots }: { slots: Slots }) => {
      return h('em', { class: 'whitespace-pre-wrap' }, slots.default?.());
    },
  },
};
const props = defineProps<{ portableData: PortableTextBlock[] }>();
const textColor = ref('#40aa1d'); // --www-color-primary-lighter
const { $staticImages } = useNuxtApp();
const doneImageSrc = $staticImages['done'];
</script>

<style>
/*  update Links and its underline color if it takes custom text color from sanity  */
.custom-underline-color a,
.custom-underline-color a:hover {
  text-decoration-color: var(--currentColor);
  color: var(--currentColor) !important;
}

a {
  word-break: break-word;
}
/* Change the color of link inside the background image */
.background-image-wrapper .content a {
  color: var(--www-color-primary-lighter) !important;
}

/* add a global style for quotes */
.text-one-column q {
  border-left: 2px solid #bbc3c4;
  display: block;
  padding-left: 5px;
  margin-left: -5px;
}
.text-one-column q::before {
  content: '' !important;
}
p.content .internal-link {
  display: inline !important;
}
.checklist-item:before {
  background: var(--done-image);
}

.styled-number-list {
  counter-reset: step;
}

.styled-number-list li {
  &::before {
    counter-increment: step;
    content: counter(step);
    list-style: none !important;
    padding-left: 0 !important;
    margin-left: 0 !important;
    position: absolute;
    left: 0;
    top: 0.2rem;
    width: 1.8rem;
    height: 1.8rem;
    background-color: var(--www-color-primary-lighter); /* green */
    color: white;
    font-weight: normal;
    font-size: 1rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  strong {
    display: inline-block;
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 0.3rem;
  }
}
</style>
